/** What the selected node actually is.
 *
 *  The tree row is one dense line; everything that needs more than a line lands
 *  here — the parsed condition tree, the feature-gate waterfall, a block's
 *  configuration, an escalation rule, or an AST's parse degradations.
 *
 *  Non-modal and pinned, so it stays put while the tree scrolls underneath —
 *  the same choice the timeline's inspector made, for the same reason: you are
 *  comparing this node against the rows around it.
 */
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Chip,
  Divider,
  IconButton,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import { ValueActions } from '@/components/ValueActions/ValueActions';

import { blockActions } from '../notifications/notificationLinks';
import { ConditionTree } from './ConditionTree';
import { editorLink } from './editorLink';
import { DeliveryRuleFlow } from './DeliveryRuleFlow';
import { GateWaterfall } from './GateWaterfall';
import type { AnatomyViewNode } from './model/types';

/** Spelled out rather than shown as a colour: `in_loop` in particular must not
 *  be mistaken for "did not fire". */
const VERDICT_TEXT: Record<string, string> = {
  taken: 'Taken — this rendered.',
  skipped: 'Not taken — the condition was false.',
  not_reached: 'Not reached — an ancestor branch excluded it.',
  in_loop: 'Inside a loop — per-iteration, so NOT evaluated. This is not a "did not fire".',
  error: 'The expression could not be evaluated.',
  empty: 'Rendered empty.',
  value: 'Rendered a value.',
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: 'text.secondary',
          mb: 0.5,
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Stack direction="row" spacing={1} sx={{ py: 0.25, minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{ minWidth: 108, flexShrink: 0, color: 'text.secondary' }}
      >
        {label}
      </Typography>
      <Typography variant="caption" sx={{ minWidth: 0, wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Stack>
  );
}

export function NodeDetailDrawer({
  node,
  notificationId,
  onClose,
}: {
  node: AnatomyViewNode | null;
  notificationId: number;
  onClose(): void;
}) {
  if (!node) {
    return (
      <Paper variant="outlined" component="section" aria-label="Node details" sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Select a row to see what it is.
        </Typography>
      </Paper>
    );
  }

  const block = node.block;
  const rule = node.rule;
  const branchArms = node.children.filter((c) => c.kind === 'branch');
  const link = editorLink(notificationId, node.editTarget, node.line);

  return (
    <Paper
      variant="outlined"
      component="section"
      aria-label="Node details"
      // No `position: sticky`: the panel lives in its own scrolling column (see
      // AnatomyPage), and sticky inside that would only fight it.
      sx={{ p: 2, minWidth: 0, overflowWrap: 'anywhere' }}
    >
      <Stack direction="row" alignItems="flex-start" sx={{ mb: 1.5 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ wordBreak: 'break-word' }}>
            {node.label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {node.kind}
            {node.line ? ` · line ${node.line}` : ''}
            {node.descendants ? ` · ${node.descendants} nodes beneath` : ''}
          </Typography>
          {node.withinBlock && (
            <Chip
              size="small"
              label={`from ${node.withinBlock}`}
              variant="outlined"
              sx={{ height: 18, fontSize: '0.6875rem', mt: 0.5 }}
            />
          )}
          {link && (
            <Link
              href={link.href}
              target="_blank"
              rel="noopener"
              variant="caption"
              sx={{ display: 'block', mt: 0.5 }}
            >
              {link.label} →
            </Link>
          )}
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Close details"
          sx={{ flexShrink: 0 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      {node.verdict && (
        <Section title="Verdict (traced)">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {VERDICT_TEXT[node.verdict.state]}
          </Typography>
          {node.verdict.detail && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {node.verdict.detail}
              {node.verdict.length !== undefined
                ? ` (${node.verdict.length.toLocaleString()} chars)`
                : ''}
            </Typography>
          )}
        </Section>
      )}

      {node.kind === 'branch' && (
        <Section title="Condition">
          <ConditionTree condition={node.condition} raw={node.conditionRaw} />
        </Section>
      )}

      {/* An `if` with ONE arm carries the condition itself (the branch row is
       *  folded away), so it needs the same tree — this is the majority of rows
       *  in a real template, and without it selecting one showed nothing. */}
      {node.kind === 'if' && node.condition !== undefined && (
        <Section title="Condition">
          <ConditionTree condition={node.condition} raw={node.conditionRaw} />
        </Section>
      )}

      {/* A multi-armed `if` is a switch: the arms are what you came to read, and
       *  the label deliberately shows only the subject. Lay them out in order —
       *  first match wins, which is why order is the whole story. */}
      {node.kind === 'if' && node.condition === undefined && branchArms.length > 0 && (
        <Section title={`Branches (${branchArms.length}) — first match wins`}>
          {branchArms.map((arm, i) => (
            <Box
              key={arm.id}
              sx={{
                borderLeft: 2,
                borderColor: arm.label === 'else' ? 'text.disabled' : 'primary.main',
                pl: 1,
                py: 0.5,
                mb: 0.5,
                minWidth: 0,
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                {i + 1}. {arm.label.split(' ')[0]}
              </Typography>
              {arm.label === 'else' ? (
                <Typography variant="caption" color="text.secondary">
                  everything the arms above did not match
                </Typography>
              ) : (
                <ConditionTree condition={arm.condition} raw={arm.conditionRaw} />
              )}
            </Box>
          ))}
        </Section>
      )}

      {node.kind === 'text' && node.textLength !== undefined && (
        <Section title="Literal text">
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {node.textLength.toLocaleString()} characters — a preview is shown, not the body.
          </Typography>
          <Box
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.8125rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: 200,
              overflow: 'auto',
            }}
          >
            {node.blank ? '(whitespace only)' : node.label}
          </Box>
        </Section>
      )}

      {node.unparsed && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {node.unparsed.reason === 'jinja2'
            ? 'This source is Jinja2. Its structure is not parsed — the viewer shows DTL only.'
            : 'This source is over the size cap, so no structure was parsed.'}{' '}
          {node.unparsed.bytes.toLocaleString()} bytes.
        </Alert>
      )}

      {node.errors && node.errors.length > 0 && (
        <Section title="Parse degradations">
          {node.errors.map((err, i) => (
            <Typography key={i} variant="caption" component="div" color="warning.main">
              {err.line ? `L${err.line}: ` : ''}
              {err.message}
            </Typography>
          ))}
        </Section>
      )}

      {block && (
        <>
          <Section title="Block">
            {/* The header already states the alias — only the divergence is news. */}
            {block.alias !== block.codename && <Field label="Codename" value={block.codename} />}
            {block.name && <Field label="Name" value={block.name} />}
            <Field label="Type" value={block.type} />
            <Field label="Provenance" value={block.source === 'direct' ? 'direct reference' : 'from a block list'} />
            <Field label="Enabled" value={block.enabled ? 'yes' : 'no'} />
            <Field
              label="On error"
              value={
                block.error_mode === 'hard'
                  ? 'hard — aborts delivery'
                  : 'soft — falls back to the default value'
              }
            />
            {block.default_value != null && (
              <Field label="Default" value={<code>{block.default_value}</code>} />
            )}
            {block.definition_preview != null && (
              <Field label="Definition" value={<code>{block.definition_preview}</code>} />
            )}
            <Field label="Definition size" value={`${block.definition_length.toLocaleString()} chars`} />
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Link
                href={`/notifications/blocks?edit=${block.block_id}`}
                target="_blank"
                rel="noopener"
                variant="caption"
              >
                Open this block →
              </Link>
              {/* The codename is what a template writes and what the
                  notification grid filters by — copy it, or find everyone
                  else using it. */}
              <Typography variant="caption" color="text.secondary">
                <ValueActions actions={blockActions({ id: block.block_id, codename: block.codename })}>
                  {block.codename}
                </ValueActions>
              </Typography>
            </Stack>
          </Section>
          <Divider sx={{ mb: 2 }} />
          <Section title="Feature gate">
            {block.feature ? (
              <GateWaterfall feature={block.feature} levels={node.gateLevels} />
            ) : (
              <Typography variant="caption" color="text.secondary">
                Not gated behind a feature — it renders whenever it is referenced.
              </Typography>
            )}
          </Section>
        </>
      )}

      {rule && node.methods && (
        <Section title="Escalation">
          <DeliveryRuleFlow rule={rule} methods={node.methods} active={true} />
        </Section>
      )}

      {node.kind === 'advanced' && node.advanced && (
        <Section title="Delivery-method ordering">
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
            <Chip
              size="small"
              label={node.advanced.ordering_label}
              color={node.advanced.active ? 'primary' : 'default'}
              variant={node.advanced.active ? 'filled' : 'outlined'}
              sx={{ height: 20, fontSize: '0.6875rem' }}
            />
            {!node.advanced.active && (
              <Typography variant="caption" color="warning.main">
                rules stored but inactive
              </Typography>
            )}
          </Stack>
          {node.advanced.rules.map((r) => (
            <Box key={r.id} sx={{ mb: 1 }}>
              <DeliveryRuleFlow
                rule={r}
                methods={node.methods ?? []}
                active={node.advanced!.active}
              />
            </Box>
          ))}
        </Section>
      )}
    </Paper>
  );
}
